import type { SearchRow } from '@bloo-team/api-client'
import type { SignalSeverity } from './signal-model'

/**
 * The pivot graph engine (docs/hunts-requirements.md E5, decision H10):
 * entities discovered while hunting become nodes; expanding a node fans a
 * pivot query out over the hunt's scope and its result's entities join the
 * graph, edged back to the node they came from.
 *
 * Everything here is PURE — extraction, graph algebra, layout. The cockpit
 * owns the jobs and the store; the canvas owns nothing but pixels. Layout is
 * deterministic by contract (ADR in docs/hunts-build-plan.md): same graph
 * in, same coordinates out, which is what makes it testable.
 */

export type EntityKind = 'user' | 'host' | 'ip' | 'domain' | 'hash' | 'other'

export interface GraphNode {
  /** `${kind}:${value}` — identity, dedupe key, layout order. */
  id: string
  kind: EntityKind
  value: string
  /** The result field the entity was first read from. */
  field: string
  /** True once this node's pivot ran — its neighbours are on the graph. */
  expanded: boolean
  /** Mini-summary captured when the node's pivot result arrived. */
  summary?: { hits: number; firstSeen: string | null; lastSeen: string | null }
  /* Signals storyline graph (docs/signals.md): all optional; absent = today's hunt look. */
  /** Drawn shape; default circle. */
  shape?: 'circle' | 'diamond'
  /** Fill token overriding the kind colour. */
  tone?: string
  /** Short text beside the node (×N merged signals). */
  badge?: string
  /** Storyline the node belongs to (the storyline canvas lights, dims and focuses by it). */
  group?: string
  /** Storyline glyph (components/signal/force): what the storyline node is and does. */
  glyph?: NodeGlyph
  /** Hover / accessible name overriding the kind · value default. */
  title?: string
  /** Storyline signal: the hover card's second line and time. */
  detail?: { tactic: string; technique: string; start: number | null }
}

export type NodeRole = 'signal' | 'suspect' | 'target' | 'compromised'

/**
 * Storyline glyph data (docs/signals.md, Graph): signals are sonar hexes,
 * suspects emitters, targets receiver reticles, compromised both. Every
 * count is plain data — the canvas decides how it reads (emitter aim comes
 * from the live layout, components/signal/force/paint.ts).
 */
export interface NodeGlyph {
  role: NodeRole
  /** Signal: core colour, ring count and ping period. */
  severity?: SignalSeverity
  /** Entity: incoming links (signals that hit it) — bullseye rings filled. */
  hits?: number
  /** Entity: outgoing links (signals it fired) — emitter arcs. */
  fired?: number
  /** Signal: merged count (×N), capped at 4 — staggered pings per cycle. */
  pings?: number
  /** Entity field (Host, User, …) — the core pictogram. */
  kind?: string
}

export interface GraphEdge {
  from: string
  to: string
  /** The pivot query that discovered the target — reproducibility. */
  viaQuery: string
  /** Signals storyline graph: draw an arrowhead at `to`. Absent = plain line. */
  directed?: boolean
}

export interface HuntGraph {
  nodes: GraphNode[]
  edges: GraphEdge[]
}

export const emptyGraph = (): HuntGraph => ({ nodes: [], edges: [] })

export const nodeId = (kind: EntityKind, value: string): string => `${kind}:${value}`

/* ------------------------------------------------------------- extraction */

const IPV4 = /^(?:\d{1,3}\.){3}\d{1,3}$/
const HASH = /^[0-9a-f]{32}$|^[0-9a-f]{40}$|^[0-9a-f]{64}$/i
const DOMAINISH = /^(?=.{4,253}$)[a-z0-9][a-z0-9-]*(\.[a-z0-9][a-z0-9-]*)+$/i

/** Field-name heuristics, checked in order — first hit wins. */
const FIELD_KIND: Array<[RegExp, EntityKind]> = [
  [/user|account|login|logon/i, 'user'],
  [/host|system|computer|machine|workstation/i, 'host'],
  [/domain|fqdn/i, 'domain'],
  // Word-bounded or edge "ip" so SrcIP/ip_addr hit while Description misses.
  [/\bip\b|^ip|ip$|address/i, 'ip'],
  [/hash|md5|sha1|sha256/i, 'hash'],
]

/**
 * Classify one field/value pair. The VALUE gets the casting vote where it is
 * unambiguous (an IPv4 in a "Host" column is still an ip; a hash is a hash
 * whatever the column) — field names are per-stream and dirty.
 */
export function classifyEntity(field: string, value: string): EntityKind | null {
  const v = value.trim()
  if (!v || v.length > 253) return null
  if (IPV4.test(v)) return 'ip'
  if (HASH.test(v)) return 'hash'
  for (const [re, kind] of FIELD_KIND) {
    if (re.test(field)) return kind
  }
  if (DOMAINISH.test(v)) return 'domain'
  return null
}

export interface ExtractedEntity {
  kind: EntityKind
  value: string
  field: string
  /** How many result rows carried it — extraction keeps the heavy hitters. */
  count: number
}

/** Values that pivot to noise, not entities. */
const VALUE_STOPLIST = new Set(['', '-', 'unknown', 'n/a', 'none', 'null', '0.0.0.0', '127.0.0.1', 'localhost'])

/**
 * One pass over a result page: every classifiable field/value pair counted,
 * top `max` by row count returned. Synthetic columns and the raw line never
 * classify (their field names miss every heuristic and their values are
 * free text).
 */
export function extractEntities(
  fields: readonly string[],
  rows: readonly SearchRow[],
  max = 12,
): ExtractedEntity[] {
  const seen = new Map<string, ExtractedEntity>()
  for (const row of rows) {
    for (const f of fields) {
      const raw = row[f]
      if (raw === null || raw === undefined || typeof raw === 'boolean') continue
      const value = String(raw).trim()
      if (VALUE_STOPLIST.has(value.toLowerCase())) continue
      const kind = classifyEntity(f, value)
      if (!kind) continue
      const id = nodeId(kind, value)
      const cur = seen.get(id)
      if (cur) cur.count += 1
      else seen.set(id, { kind, value, field: f, count: 1 })
    }
  }
  return [...seen.values()].sort((a, b) => b.count - a.count || a.value.localeCompare(b.value)).slice(0, max)
}

/* ------------------------------------------------------------ graph algebra */

/**
 * Fold extracted entities into the graph: new nodes appended, existing ones
 * untouched (first classification wins), one edge per parent->child pair —
 * re-discovery never duplicates. With a null parent the entities SEED the
 * graph (no edges). Pure — returns a new graph.
 */
export function addEntities(
  graph: HuntGraph,
  parentId: string | null,
  entities: readonly ExtractedEntity[],
  viaQuery: string,
): HuntGraph {
  const nodes = [...graph.nodes]
  const edges = [...graph.edges]
  const byId = new Map(nodes.map((n) => [n.id, n]))
  const edgeKeys = new Set(edges.map((e) => `${e.from}->${e.to}`))
  for (const ent of entities) {
    const id = nodeId(ent.kind, ent.value)
    if (id === parentId) continue // a node never edges to itself
    if (!byId.has(id)) {
      const node: GraphNode = { id, kind: ent.kind, value: ent.value, field: ent.field, expanded: false }
      nodes.push(node)
      byId.set(id, node)
    }
    if (parentId && !edgeKeys.has(`${parentId}->${id}`)) {
      edges.push({ from: parentId, to: id, viaQuery })
      edgeKeys.add(`${parentId}->${id}`)
    }
  }
  return { nodes, edges }
}

/** Mark a node expanded and attach its pivot summary. Pure. */
export function markExpanded(
  graph: HuntGraph,
  id: string,
  summary: GraphNode['summary'],
): HuntGraph {
  return {
    ...graph,
    nodes: graph.nodes.map((n) => (n.id === id ? { ...n, expanded: true, ...(summary ? { summary } : {}) } : n)),
  }
}

/* ----------------------------------------------------------------- layout */

export interface LayoutPoint {
  x: number
  y: number
}

/**
 * Deterministic radial layout: BFS depth from the seed nodes (no incoming
 * edges) makes the ring, id order makes the angle. Disconnected nodes ring
 * outward after the connected ones. Coordinates land in a unit box scaled
 * by `size` with the seeds' ring at the center.
 */
export function radialLayout(graph: HuntGraph, size = 1000): Map<string, LayoutPoint> {
  const out = new Map<string, LayoutPoint>()
  if (!graph.nodes.length) return out
  const cx = size / 2
  const cy = size / 2

  const hasIncoming = new Set(graph.edges.map((e) => e.to))
  const roots = graph.nodes.filter((n) => !hasIncoming.has(n.id)).map((n) => n.id).sort()
  const children = new Map<string, string[]>()
  for (const e of [...graph.edges].sort((a, b) => a.to.localeCompare(b.to))) {
    const list = children.get(e.from) ?? []
    if (!list.includes(e.to)) list.push(e.to)
    children.set(e.from, list)
  }

  // BFS depth per node, roots first (or every node when the graph is one cycle).
  const depth = new Map<string, number>()
  const queue: string[] = roots.length ? [...roots] : [...graph.nodes.map((n) => n.id)].sort().slice(0, 1)
  for (const r of queue) depth.set(r, 0)
  while (queue.length) {
    const id = queue.shift()!
    for (const c of children.get(id) ?? []) {
      if (!depth.has(c)) {
        depth.set(c, depth.get(id)! + 1)
        queue.push(c)
      }
    }
  }
  // Anything unreached (disconnected) sits one ring past the deepest.
  const maxDepth = Math.max(0, ...depth.values())
  for (const n of graph.nodes) if (!depth.has(n.id)) depth.set(n.id, maxDepth + 1)

  // Ring membership, id-sorted for angular determinism.
  const rings = new Map<number, string[]>()
  for (const [id, d] of depth) {
    const ring = rings.get(d) ?? []
    ring.push(id)
    rings.set(d, ring)
  }
  const ringCount = Math.max(...rings.keys()) + 1
  const ringGap = (size / 2 - 80) / Math.max(1, ringCount - 1 || 1)

  for (const [d, ids] of rings) {
    ids.sort()
    const r = d === 0 && ids.length === 1 ? 0 : Math.max(90, d * ringGap || 90)
    ids.forEach((id, i) => {
      // -90° start so the first node sits at 12 o'clock; golden-angle-free,
      // plain even slices — determinism beats aesthetics here.
      const a = (2 * Math.PI * i) / ids.length - Math.PI / 2
      out.set(id, {
        x: Math.round(cx + r * Math.cos(a)),
        y: Math.round(cy + r * Math.sin(a)),
      })
    })
  }
  return out
}

/**
 * One storyline (node `group`) on a force-laid canvas (Signals storylines):
 * the hover chip's text and accessible name, and the node labelled when the
 * canvas is zoomed out.
 */
export interface GraphGroup {
  /** GraphNode.group of the nodes in this storyline. */
  id: string
  /** Short chip text (`Focus · <caption>`). */
  caption: string
  /** Full name — the chip's accessible name (`Focus storyline <label>`) and tooltip. */
  label: string
  /** The storyline's main entity: still labelled when the canvas is zoomed out. */
  mainId?: string
}

/* ------------------------------------------------------------ serialization */

const str = (v: unknown): string => (typeof v === 'string' ? v : '')

/** Tolerant reader for the graph stored on a hunt — bad records drop out. */
export function readGraph(v: unknown): HuntGraph | undefined {
  if (typeof v !== 'object' || v === null) return undefined
  const o = v as Record<string, unknown>
  const kinds: EntityKind[] = ['user', 'host', 'ip', 'domain', 'hash', 'other']
  const nodes = (Array.isArray(o.nodes) ? o.nodes : [])
    .map((n): GraphNode | null => {
      if (typeof n !== 'object' || n === null) return null
      const r = n as Record<string, unknown>
      const kind = str(r.kind) as EntityKind
      if (!str(r.id) || !str(r.value) || !kinds.includes(kind)) return null
      const s = r.summary as Record<string, unknown> | undefined
      return {
        id: str(r.id),
        kind,
        value: str(r.value),
        field: str(r.field),
        expanded: r.expanded === true,
        ...(typeof s === 'object' && s !== null && typeof s.hits === 'number'
          ? {
              summary: {
                hits: s.hits,
                firstSeen: str(s.firstSeen) || null,
                lastSeen: str(s.lastSeen) || null,
              },
            }
          : {}),
      }
    })
    .filter((n): n is GraphNode => n !== null)
  const ids = new Set(nodes.map((n) => n.id))
  const edges = (Array.isArray(o.edges) ? o.edges : [])
    .map((e): GraphEdge | null => {
      if (typeof e !== 'object' || e === null) return null
      const r = e as Record<string, unknown>
      if (!str(r.from) || !str(r.to)) return null
      return { from: str(r.from), to: str(r.to), viaQuery: str(r.viaQuery) }
    })
    .filter((e): e is GraphEdge => e !== null && ids.has(e.from) && ids.has(e.to))
  return { nodes, edges }
}
